import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import {  StringAvatar, } from "../avatar";
import { LoadingButton } from '@mui/lab';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { fontModules, fontFormats, smallStyle, registerFonts } from "../../utils/QuillConfig";
import { findInvalidEmails, formatInvalidEmailLines } from "../../utils/emailUtils";
import usePrevious from "../hook/usePrevious";

import { AlertDialog, DialogTitleClose, PaperComponent, ConfirmDialog } from './';
import { FormInputText, FormInputAutoComplete, FormInputMultipleSelect } from '../form';
import CustomInputSelect from '../custom/select/CustomInputSelect';
import * as userActions from "../../store/user";
import * as addressBookActions from "../../store/addressBook";
import * as gclientActions from "../../store/gclient";
import * as gsendBasketActions from "../../store/gsendBasket";
import AddressBookDialogRegister from '../Profile/AddressBookDialogRegister';
import CertifiedStampPlacementDialog from '../G04docuSubMatTemplate/CertifiedStampPlacementDialog';
import CertifiedStampPrintPane from '../G04docuSubMatTemplate/CertifiedStampPrintPane';
import { STAMP_LAYOUT_COMPACT, STAMP_LAYOUT_FULL } from '../G04docuSubMatTemplate/certifiedStampSpec';

const isPdfFile = (file) => {
  const name = file?.name || '';
  return file?.type === 'application/pdf' || /\.pdf$/i.test(name);
};

const placementIdOf = (file) => {
  if (!file) return '';
  if (!file.__placementId) {
    Object.defineProperty(file, '__placementId', {
      value: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    });
  }
  return file.__placementId;
};

const isPdfPath = (path) => /\.pdf$/i.test(String(path || ''));

const placementSourceKeyOf = (row) => `file:${row?.g04docuId || row?.id || ''}`;

const pagePlacementsForPrefix = (stampPlacements, prefix) => {
  const pagePlacements = {};
  Object.entries(stampPlacements || {}).forEach(([key, pos]) => {
    if (!prefix || !key.startsWith(prefix) || !key.endsWith('::raw') || !pos) return;
    const page = key.slice(prefix.length, key.length - '::raw'.length);
    if (page) pagePlacements[page] = pos;
  });
  return pagePlacements;
};

const mailCardHeaderSx = {
  bgcolor: '#eaeaea',
  borderBottom: '1px solid',
  borderColor: 'divider',
  px: 2,
  py: 0.75,
};

const defaultValues = {
  subject: "",
  html: "",
  supplierLabel: "제공사",
  supplier: "",
  recipientLabel: "제출처",
  recipient: "",
  siteLabel: "현장명",
  site: "",
  phrase: "",
};

const SendMailDialog = ({
  open,
  onClose,
  gsendBaskets,
  setGSendBaskets,
  /** 폴더형 서류보내기: 문서별 원본대조필 위치. 없으면 레거시 동작 */
  folderStampItems = null,
  /** 폴더형 서류보내기·전송바구니: 서류 목록 / 원본대조필 / 메일 쓰기 */
  threeColumn = false,
  /** 등록 서류를 이 메일에서 뺄 때. 폴더형 서류보내기는 목록 체크를 푼다. */
  onRemoveRegistered,
}) => {

  // 컴포넌트 마운트 시 폰트 등록
  useEffect(() => {
    const Quill = ReactQuill.Quill;
    registerFonts(Quill);
  }, []);

  const uploadFilesBoxRef = useRef();
  const inputFilesRef = useRef();

  // 상태 관리
  const [gclients, setGClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [signOption, setSignOption] = useState('CERTIFIED_TRUE_COPY_AND_SIGN');
  const [stampLayout, setStampLayout] = useState(STAMP_LAYOUT_FULL);
  const [stampPlacements, setStampPlacements] = useState({});
  const [placementOpen, setPlacementOpen] = useState(false);
  const [sendItems, setSendItems] = useState([]);
  const [mobilePhase, setMobilePhase] = useState('docs');
  const [trackedOpen, setTrackedOpen] = useState(open);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const mobileSteps = Boolean(threeColumn) && isMobile;
  if (open !== trackedOpen) {
    setTrackedOpen(open);
    if (open) {
      setSendItems(Array.isArray(gsendBaskets) ? gsendBaskets : []);
      setMobilePhase('docs');
    }
  }
  const useFolderStamp = Boolean(folderStampItems?.length)
    || sendItems.some((row) => row.documentType === 'SUBMAT_FOLDER');
  const mixedStamp = useFolderStamp
    && sendItems.some((row) => row.documentType && row.documentType !== 'SUBMAT_FOLDER');
  const folderPlacementItems = useMemo(
    () => sendItems
      .filter((row) => row.documentType === 'SUBMAT_FOLDER' && isPdfPath(row.documentPath))
      .map((row) => ({
        id: row.g04docuId || row.id,
        folderId: row.folderId || row.g04docuId || row.id,
        placementSourceKey: placementSourceKeyOf(row),
        documentPath: row.documentPath,
        parentItemId: '',
        included: true,
        hasCurrentFile: true,
        folderName: row.fileName || row.name?.name || '서류',
      })),
    [sendItems],
  );

  useEffect(() => {
    if (!open) return;
    setStampLayout(STAMP_LAYOUT_FULL);
    setStampPlacements({});
    setPlacementOpen(false);
  }, [open]);
  const [myCertified, setMyCertified] = useState(false);
  const [gclientId, setGClientId] = useState("");
  const [URLThumbnail, setURLThumbnail] = useState("");
  const [emptyRecipient, setEmptyRecipient] = useState(false);
  const [emptySite, setEmptySite] = useState(false);
  const [mailTos, setMailTos] = useState([]);
  const [mailCcs, setMailCcs] = useState([]);
  const [mailDirect, setMailDirect] = useState(false);
  const [mailToDirect, setMailToDirect] = useState([]);
  const [mailCcDirect, setMailCcDirect] = useState([]);
  const [mailCcInternals, setMailCcInternals] = useState([]);
  const [addedFiles, setAddedFiles] = useState([]);
  const placementExtras = useMemo(
    () => (useFolderStamp ? addedFiles : []).filter(isPdfFile).map((file) => ({
      id: placementIdOf(file),
      file,
      included: true,
      title: file.name,
    })),
    [addedFiles, useFolderStamp],
  );
  const [includeGTestComparisons, setIncludeGTestComparisons] = useState(true);
  const [addressOptions, setAddressOptions] = useState([]);
  const [emailOptionsTo, setEmailOptionsTo] = useState([]);
  const [emailOptionsCc, setEmailOptionsCc] = useState([]);
  const [emailOptionsCcInternal, setEmailOptionsCcInternal] = useState([]);
  const [alertInfo, setAlertInfo] = useState({open: false, titleAlert: '', messageAlert: ''});
  const [uploadBoxStyle, setUploadBoxStyle] = useState({ border: 1, borderRadius: 2, borderColor: 'divider' });

  const [addressBookDialogOpen, setAddressBookDialogOpen] = useState(false);
  const [crudMode, setCrudMode] = useState('C');
  const [selectedRow, setSelectedRow] = useState(null);

  const dispatch = useDispatch();
  
  // Redux 상태
  const rowsAddressBooks = useSelector((state) => state.addressBook.addressBooks);
  const sessionUser = useSelector(state => state.session.sessionUser);
  
  const selectAllByGClientDirect = (gclientId) => userActions.selectAllByGClientDirect(gclientId)
  const sendWithLocalFileDirect = (formData) => gsendBasketActions.sendWithLocalFileDirect(formData)
  const sealByIdRef = useRef({});
  const loadSealClients = async (rows) => {
    const ids = [...new Set((rows || []).map((row) => row?.gclientId || row?.gclient?.id).filter(Boolean))];
    const missing = ids.filter((id) => !sealByIdRef.current[id]);
    if (missing.length > 0) {
      const list = await gclientActions.selectSealDataByIdsDirect(missing);
      const next = { ...sealByIdRef.current };
      (list || []).forEach((gclient) => {
        if (gclient?.id) next[gclient.id] = gclient;
      });
      sealByIdRef.current = next;
    }
    return sealByIdRef.current;
  };
  const selectGSendBasketsByCreatorByQuery = (creatorId) => dispatch(gsendBasketActions.selectAllByCreatorByQuery(creatorId))
  const downloadFile = (documentPath) => gclientActions.downloadDirect(documentPath)

  // Form 관리
  const { handleSubmit, control, setValue, getValues, reset } = useForm({
    defaultValues: defaultValues
  });

  // 다이얼로그가 닫힐 때 상태 초기화
  const handleClose = () => {
    // 모든 상태 초기화
    sealByIdRef.current = {};
    setGClients([]);
    setLoading(false);
    setSignOption('CERTIFIED_TRUE_COPY_AND_SIGN');
    setMyCertified(false);
    setGClientId("");
    setURLThumbnail("");
    setEmptyRecipient(false);
    setEmptySite(false);
    setMailTos([]);
    setMailCcs([]);
    setMailDirect(false);
    setMailToDirect([]);
    setMailCcDirect([]);
    setMailCcInternals([]);
    setAddedFiles([]);
    setIncludeGTestComparisons(true);
    setAddressOptions([]);
    setEmailOptionsTo([]);
    setEmailOptionsCc([]);
    setEmailOptionsCcInternal([]);
    setAlertInfo({open: false, titleAlert: '', messageAlert: ''});
    
    // 폼 초기화
    reset(defaultValues);

    setGSendBaskets([]);
    
    // 부모 컴포넌트의 onClose 호출
    onClose();
  };

  const onSubmit = async ({ mailTo, mailCc, mailCcInternal, subject, html, supplierLabel, supplier, recipientLabel, recipient, siteLabel, site, phrase }) => {
    // console.log({ mailTo, mailCc, mailCcInternal, subject, html, supplierLabel, supplier, recipientLabel, recipient, siteLabel, site, phrase })
    // return;

    await sendMail({ mailTo, mailCc, mailCcInternal, subject, html, supplierLabel, supplier, recipientLabel, recipient, siteLabel, site, phrase, includeGTestComparisons, myCertified });
  }

  const sendMail = async ({ mailTo, mailCc, mailCcInternal, subject, html, supplierLabel, supplier, recipientLabel, recipient, siteLabel, site, phrase, includeGTestComparisons, myCertified }, sendReplace) => {
    // console.log({ rowsGSendBaskets, addedFiles, mailTo, mailCc, mailCcInternal, subject, html, supplierLabel, supplier, recipientLabel, recipient, siteLabel, site, phrase, includeGTestComparisons, myCertified, sendReplace })
    // return;

    const compactStamp = useFolderStamp && stampLayout === STAMP_LAYOUT_COMPACT;
    if (signOption === 'CERTIFIED_TRUE_COPY_AND_SIGN' && !compactStamp) {
      if (!recipient) {
        setEmptyRecipient(true);
      }

      if (!site) {
        setEmptySite(true);
      }

      if (!recipient || !site) {
        setAlertInfo({
          titleAlert: "안내",
          messageAlert: `제출처나 현장명을 모두 입력해주세요.`,
          open: true,
        });
        return;
      }
    }

    const phrases = {
      recipient,
      site,
      ...(useFolderStamp ? { stampLayout, stampPlacements } : {}),
    };

    // 메일 보내기 전 유효성 체크, TODO : 더 체크할게 있는지 검토할 것
    const readyDocs = sendItems.filter((row) => row.documentPath);
    if (readyDocs.length === 0 && addedFiles.length === 0) {
      setAlertInfo({
        titleAlert: "안내",
        messageAlert: "보낼 서류가 없습니다.",
        open: true,
      });
      return;
    }

    const mailToArr = mailDirect ? mailToDirect.map(item => ({ id: item, name: item })) : (mailTo ? mailTo : []);
    if (mailToArr) {
      if (mailToArr.length <= 0) {
        setAlertInfo({
          titleAlert: "안내",
          messageAlert: `받는 사람을 입력해주세요.`,
          open: true,
        });
        return;
      }
    }

    const mailCcArr = mailDirect
      ? (mailCcDirect || []).map(item => ({ id: item, name: item }))
      : (mailCc ? mailCc : []);
    const invalidEmails = [
      ...findInvalidEmails(mailToArr),
      ...findInvalidEmails(mailCcArr),
      ...findInvalidEmails(mailCcInternal || []),
    ];
    if (invalidEmails.length > 0) {
      setAlertInfo({
        titleAlert: "안내",
        messageAlert: (
          <>
            <div>메일 주소 형식이 올바르지 않습니다.</div>
            <div style={{ marginTop: '8px', whiteSpace: 'pre-line' }}>
              {formatInvalidEmailLines(invalidEmails).join('\n')}
            </div>
          </>
        ),
        open: true,
      });
      return;
    }

    try {
      // console.log(addedFiles)
      
      setLoading(true);

      const sealClients = await loadSealClients(readyDocs);
      const selectedGClient = gclients.filter(gclient => gclient.id === gclientId); // 직접쓰기하면 gclientId = "" => selectedGClient = []

      // console.log({
      //   gclientTo: selectedGClient.length === 1 ? selectedGClient[0] : {},
      //   mailTo: mailToArr,
      //   mailCc: mailDirect ? mailCcDirect.map(item => ({ id: item, name: item })) : (mailCc ? mailCc : []),
      //   mailCcInternal,
      //   subject,
      //   html,
      //   mailDirect,
      //   gsendBaskets: rowsGSendBaskets,
      //   options: { signOption },
      //   phrases,
      // });

      const addedFileNames = [];

      const formData = new FormData();
      
      formData.append("includeGTestComparisons", includeGTestComparisons ? 'Y' : 'N');
      formData.append("myCertified", myCertified);
      
      // if (sendReplace) {
        formData.append("sendReplace", sendReplace);
      // }
      
      // mailToArr에서 첫 번째 거래선 찾기
      // id: `<${address.company}> <${address.email}> <${address.id}>`, name: `${address.name} <${address.email}>`, group: "주소록"
      //id: `<${name}> <${email}> <${id}>`, name: `회사대표메일 <${email}>`, group: "거래선" 
      const firstMailTo = mailToArr.find(mail => mail.group === "거래선"|| mail.group === "주소록");
      const firstGClientName = firstMailTo?.id.match(/<([^>]+)>/)?.[1] || '';
      //console.log("firstMailTo => ", firstMailTo);
      //const firstGClientId = firstMailTo?.id.match(/<([^>]+)>$/)?.[1] || '';
      //const firstGClient = gclients.filter(gclient => gclient.id === firstGClientId);

      formData.append("mailData", JSON.stringify({
        gclientTo: firstMailTo ? { name: firstGClientName } : {}, 
        mailTo: mailToArr,
        mailCc: mailDirect ? mailCcDirect.map(item => ({ id: item, name: item })) : (mailCc ? mailCc : []),
        mailCcInternal,
        subject,
        html,
        // TODO : 보내는 시점에 인감과 프로젝트 날인 정보가 변경되었다면 이미 저장된 데이터로 전송하므로 최신정보가 적용되지 않는다.
        gsendBaskets: readyDocs
                        .map(row => {
                          const owner = sealClients[row.gclientId] || sealClients[row.gclient?.id];
                          const folderRow = row.documentType === 'SUBMAT_FOLDER';
                          const pagePlacements = folderRow
                            ? pagePlacementsForPrefix(stampPlacements, `${placementSourceKeyOf(row)}::`)
                            : null;
                          return {
                            ...row,
                            ...(folderRow ? { stampPlacements: pagePlacements, stampLayout } : {}),
                            gclient: owner ? {
                                        id: owner.id || '',
                                        name: owner.name || '',
                                        email: owner.email || '',
                                        signDocumentPath: owner.signDocumentPath || '',
                                        phrases: owner.phrases || {
                                            phrase: '',
                                            supplier: '',
                                            supplierLabel: ''
                                        }
                                    } : (row.gclient || {}),
                          }
                        }),
        options: { signOption },
        ...(useFolderStamp ? {
          addedStampLayout: stampLayout,
          addedStampPlacements: addedFiles.map((file) => (
            isPdfFile(file)
              ? pagePlacementsForPrefix(stampPlacements, `extra:${placementIdOf(file)}::`)
              : null
          )),
        } : {}),
        phrases,
      }));
      
      // addedFiles는 배열이 아니고 객체이므로 아래와 같이 배열로 변환
      console.log(addedFiles)

      
      addedFiles.forEach((file) => { // formData.append('addedFiles', addedFiles); 이렇게 넘기면 안됨
        formData.append("addedFiles", file);
        addedFileNames.push(file.name)
      });
      
      // console.log(addedFileNames);
      formData.append("addedFileNames", JSON.stringify(addedFileNames));

      const res = await sendWithLocalFileDirect(formData);


      console.log(res)
      if (res) {
        if (res.gsendG04docu) {
          setLoading(false);
          console.log(res.gsendG04docu)
          if (res.gsendG04docu?.id) {

            console.log(mailToArr)
            console.log(rowsAddressBooks)
            // 직접 입력한 이메일 주소 확인
            const manuallyAddedEmails = mailToArr.filter(email => 
              // id가 없고 이메일이 있는 경우는 직접 입력
              email.group === '사용자 입력' && 
              email.name && 
              // 주소록에 존재하지 않는 이메일만 필터링
              !rowsAddressBooks.some(addressBook => addressBook.email === email.name)
            );
            console.log(manuallyAddedEmails)

            if (manuallyAddedEmails.length > 0) {
              // 먼저 메일 전송 완료 알림
              setAlertInfo({
                titleAlert: "안내",
                messageAlert: (
                  <div>
                    <div>메일이 전송되었습니다.</div>
                    <div style={{ marginTop: '10px' }}>
                      직접 입력한 이메일을 주소록에 추가하시겠습니까?
                    </div>
                  </div>
                ),
                type: 'confirm',
                open: true,
                confirmText: '예',
                cancelText: '아니오',
                handleConfirm: async() => {
                  setCrudMode('C'); // 등록 모드로 설정
                  // 첫 번째 이메일 정보로 초기값 설정
                  const firstEmail = manuallyAddedEmails[0];
                  setSelectedRow({
                    email: firstEmail.name || '',
                    name: '',
                    phone: '',
                    company: '',
                    duties: '',
                    publicYN: false
                  });
                  setAddressBookDialogOpen(true);
                  // 주소록 다이얼로그가 열린 후에 메일 다이얼로그 닫기
                  handleClose();
                }, 
                handleClose: () => {
                  console.log('handleClose')
                  // 주소록 다이얼로그가 열린 후에 메일 다이얼로그 닫기
                  handleClose();
                }
                
              });
            } else {

              setAlertInfo({
                titleAlert: "안내",
                messageAlert: `메일이 전송되었습니다.`,
                open: true
              });
              
              // 메일 전송 완료 후 300ms 뒤에 주소록 다이얼로그 닫기
              setTimeout(() => handleClose(), 300);
            }
          }
        } else {
          console.log(res)
          setAlertInfo({
            titleAlert: res?.title || "안내",
            messageAlert: res?.message || "메일 전송에 실패했습니다.",
            open: true,
          });
        }
      } else {
        console.log('메일 전송 실패')
        setAlertInfo({
          titleAlert: "안내",
          messageAlert: "메일 전송에 실패했습니다.",
          open: true,
        });
      }

      setLoading(false);
    } catch (e) {
      console.log(e)
      setLoading(false);
      const message = e?.message || e?.serverResponse?.message;
      setAlertInfo({
        titleAlert: "안내",
        messageAlert: message || "메일 전송에 실패했습니다.",
        open: true,
      });
    }
  }

  // 주소록 관련 함수
  const selectAllAddressBook = (searchWord, sort, initConst) => 
    dispatch(addressBookActions.selectAll(searchWord, sort, initConst));

  useEffect(() => {
    convertAddressBooks();
  }, [rowsAddressBooks]);

  const convertAddressBooks = () => {
    console.log('convertAddressBooks', rowsAddressBooks)
    if (rowsAddressBooks && Array.isArray(rowsAddressBooks) && rowsAddressBooks.length > 0) {
      const uniqueAddresses = Array.from(
        new Map(
          rowsAddressBooks.map(address => [
            address.email,
            {
              id: `<${address.company}> <${address.email}> <${address.id}>`,
              name: `${address.name} <${address.email}>`,
              group: "주소록"
            }
          ])
        ).values()
      );
      
      setAddressOptions(uniqueAddresses);
      return uniqueAddresses;
    }
    setAddressOptions([]);
    return [];
  };

  // 이벤트 핸들러
  const handleChangeSignOption = (e, value) => {
    setSignOption(value);
    if (value === 'NONE') {
      setMyCertified(false);
    }
  };

  const handleFolderSignOption = (value) => {
    setSignOption(value);
    if (value === 'NONE') {
      setMyCertified(false);
    }
  };

  const handleChangeMyCertified = (e, checked) => {
    setMyCertified(checked);
  };

  const handleChangeRecipient = (e) => {
    const { value } = e.target;
    if (value) {
      setEmptyRecipient(false);
    } else {
      setEmptyRecipient(true);
    }
    setValue("recipient", value);
  };

  const handleChangeSite = (e) => {
    const { value } = e.target;
    if (value) {
      setEmptySite(false);
    } else {
      setEmptySite(true);
    }
    setValue("site", value);
  };

  const handleChangeGClient = (e, value) => {
    if (!value) {
      setValue("recipient", "");
      return;
    }

    const { id: gclientId, label } = value;
    setGClientId(gclientId);
    setValue("recipient", label);
  };

  const handleFileChange = (e) => {
    const incoming = [...e.target.files];
    incoming.forEach(placementIdOf);
    setAddedFiles(addedFiles.concat(incoming));
    e.target.value = "";
  };

  const handleDeleteAddedFile = (e, idx) => {
    e.stopPropagation();
    setAddedFiles(addedFiles.filter((file, i) => idx !== i));
  };

  const handleRemoveSendItem = (e, item) => {
    e.stopPropagation();
    setSendItems((prev) => prev.filter((row) => row.id !== item.id));
    onRemoveRegistered?.(item);
  };

  const handleIncludeGTestComparisons = (e, checked) => {
    setIncludeGTestComparisons(checked);
  };

  // useEffect
  useEffect(() => {

    const emailOption = async () => {
      console.log({ uploadFilesBoxRef, inputFilesRef })

      const uploadBox = uploadFilesBoxRef.current;
      const input = inputFilesRef.current;
      
      if (uploadBox) {
        // alert("uploadBox")
      }
      if (input) {
        // alert("input")
      }
      console.log({ uploadBox, input })

      if (uploadBox && input) {
        // alert("222")
        console.log({ uploadBox, input })

        const dropHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
      
          setUploadBoxStyle({ border: 1, borderRadius: 2, borderColor: 'divider' })
      
          const { files } = e.dataTransfer;
          console.log(files)
          if (!files || !files[0]) return;
          
          // createImageURLAndUpload(files[0]);
        };


        const changeHandler = async (e) => {        
          e.preventDefault();
          e.stopPropagation();
      
          const { files } = e.target;
          console.log(files)
      
          if (!files || !files[0]) return;
          
          // createImageURLAndUpload(files[0]);
      
          e.target.value = ''; // 같은 파일을 올리면 onChange 이벤트가 발생하지 않으므로 초기화
        };
        
        uploadBox.addEventListener("drop", dropHandler);
        // uploadBox.addEventListener("dragover", dragOverHandler);
        // uploadBox.addEventListener("dragleave", dragLeaveHandler);
        // uploadBox.addEventListener("mouseover", mouseOverHandler);
        // uploadBox.addEventListener("mouseleave", mouseLeaveHandler);
        
        input.addEventListener("change", changeHandler);
        
        return () => {
          uploadBox.removeEventListener("drop", dropHandler);
          // uploadBox.removeEventListener("dragover", dragOverHandler);
          // uploadBox.removeEventListener("dragleave", dragLeaveHandler);
          // uploadBox.removeEventListener("mouseover", mouseOverHandler);
          // uploadBox.removeEventListener("mouseleave", mouseLeaveHandler);
          
          input.removeEventListener("change", changeHandler);
        };
      }
    
      const selectGClients = async () => {
        const resGClients = await gclientActions.selectComboDataDirect(['id', 'name', 'email']);
        setGClients(resGClients || []);
      }

      selectGClients();
      loadSealClients(gsendBaskets).catch((error) => {
        console.error(error);
      });

      console.log('sessionUser', sessionUser)
      // 내부참조
      const { id, email, name } = sessionUser; // sessionUser의 id는 회원사 관리자 id임 (접속한 계정이 회원사 사용자이더라도...)
      const users = await selectAllByGClientDirect(id);
      const emailList = [];
      //console.log('email ::::::::::::::::::::::::::::::::: ', email)
      if (email) {
        emailList.push({id: `<${name}> <${email}> <${id}>`, name: `회사대표메일 <${email}>`, group: "거래선" }); // code가 아니라 id임에 유의
      }

      console.log('users', users)
      if (users.length > 0) {
        users.forEach(user => {
          const { name, userId, gclient } = user;
          emailList.push({ id: `<${gclient.name}> <${userId}> <${gclient.id}>`, name: `${name} <${userId}>`, group: "거래선" });
        })
      }

      // console.log(emailList)
      if (emailList.length > 0) {
        setEmailOptionsCcInternal(emailList);
      } else {
        setEmailOptionsCcInternal([]);
      }

      const none = gsendBaskets.filter(row => !row.documentPath);
      if (none.length > 0) {
        setAlertInfo({
          titleAlert: "안내",
          messageAlert: (<>
            <span style={{ color: "#1976d2" }}>{"등록되어 있지 않은 문서가 포함"}</span><span>{"되어 있습니다."}</span><br />
            <span>{"메일 보낼 때 "}</span><span style={{ color: "#1976d2" }}>{"등록되지 않은 문서는 제외"}</span><span>{"됩니다."}</span>
          </>),
          open: true,
        });
      }
    }

    const loadAddressBooks = async () => {
      try {
        // 주소록 전체 조회
        await selectAllAddressBook();
        // convertAddressBooks();

        if (sessionUser) {
          if (sessionUser["phrases"]) {
            const { supplierLabel, supplier, phrase } = sessionUser["phrases"];
            setValue("supplierLabel", supplierLabel);
            setValue("supplier", supplier);
            setValue("phrase", phrase);
          }

          const { sessionUserLoginType } = sessionUser;
          const mailSettings = sessionUserLoginType === "USER" ? 
            sessionUser?.user?.mailSettings : 
            sessionUser?.mailSettings;

          if (mailSettings?.footer?.useYn) {
            setValue('html', mailSettings.footer.message ?? "");
          }
        }
      } catch (error) {
        console.error('Error loading address books:', error);
      }
    };

    const selectSignDocument = async () => {
      const { id, type, signDocumentPath, sessionUserLoginType } = sessionUser;
      let creatorId = "";
      if (sessionUserLoginType === 'GCLIENT') {
        creatorId = id;
      } else if (sessionUserLoginType === 'USER') {
        if (sessionUser.user) {
          creatorId = sessionUser.user.id;
        }
      }
      // if (type === 'ADMIN' || type === 'GCLIENT') {
        await selectGSendBasketsByCreatorByQuery(creatorId);
        
        if (signDocumentPath) { // TODO : 해당 문서의 소유 회원사의 인감이 각 문서에 날인되어야 하므로 아래가 필요할지 검토
          const res = await downloadFile(signDocumentPath); // TODO : 서버에 파일이 없을 경우 오류나서 렌더링되지 않으므로 try catch 할 것
          const blob = await res.blob(); // res.blob()는 비동기 함수이므로 await 필요
          const url = URL.createObjectURL(blob);
          setURLThumbnail(url);
        }
    }

    if (open) {
      emailOption();
      loadAddressBooks();
      // convertAddressBooks();
      selectSignDocument();
    }
  }, [open, sessionUser]);

  const prevGClientId = usePrevious(gclientId);

  useEffect(
    async () => {
      if (prevGClientId !== gclientId) {
        setMailTos([]);
        setMailCcs([]);
      }

      // console.log({ prevGClientId, gclientId })
      const selectedGClient = gclients.filter(gclient => gclient.id === gclientId);
      console.log(selectedGClient)
      if (selectedGClient.length === 1) {
        const { id, email, name, inCharges } = selectedGClient[0];

        const emailList = [];
        if (email) {
          emailList.push({ id: `<${name}> <${email}> <${id}>`, name: `회사대표메일 <${email}>`, group: "거래선" });
        }

        // if (inCharges && Array.isArray(inCharges)) {
        //   inCharges.forEach(inCharge => {
        //     const { inChargeName, inChargeEmail } = inCharge;
        //     emailList.push({ id: `${inChargeName} <${inChargeEmail}>`, name: `${inChargeName} <${inChargeEmail}>` });
        //   })
        // }
        const users = await selectAllByGClientDirect(id);
        if (users.length > 0) {
          users.forEach(user => {
            const { name, userId, gclient } = user;
            emailList.push({ id: `<${gclient.name}> <${userId}> <${gclient.id}>`, name: `${name} <${userId}>`, group: "거래선" });
          })
        }

        // TODO : inCharges 사용하지 않으므로 바꾸어야 함
        if (emailList.length > 0) {
          setEmailOptionsTo(emailList);
          setEmailOptionsCc(emailList);
        } else {
          setEmailOptionsTo([]);
          setEmailOptionsCc([]);
        }
      }
    }, [gclientId]
  )

  // useEffect를 추가하여 gsendBaskets이 변경될 때마다 체크 상태를 업데이트
  useEffect(() => {
    const hasTestComparison = sendItems.some(item => item.gtestComparisonDocumentPath);
    setIncludeGTestComparisons(hasTestComparison);
  }, [sendItems]);

  const mobileColumnSx = (phase) => (mobileSteps ? {
    display: mobilePhase === phase ? 'flex' : 'none',
    flex: 1,
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  } : {});

  const mobileStepTitle = mobilePhase === 'stamp'
    ? '원본대조필'
    : mobilePhase === 'mail'
      ? '메일 쓰기'
      : '서류 목록';

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperComponent={mobileSteps ? undefined : PaperComponent}
        fullScreen={mobileSteps}
        aria-labelledby="draggable-dialog-title"
        maxWidth={threeColumn ? false : 'sm'}
        fullWidth={!mobileSteps}
        PaperProps={mobileSteps ? {
          sx: { display: 'flex', flexDirection: 'column' },
        } : (threeColumn ? {
          sx: {
            width: { xs: 'calc(100vw - 32px)', md: 'min(1280px, calc(100vw - 64px))' },
            maxWidth: 'calc(100vw - 32px)',
            height: { md: 'min(880px, calc(100% - 64px))' },
            maxHeight: 'calc(100% - 64px)',
          },
        } : undefined)}
      >
        <DialogTitleClose
          id="draggable-dialog-title"
          onClose={handleClose}
          style={mobileSteps ? undefined : { cursor: 'move' }}
          sx={mobileSteps ? {
            position: 'relative',
            fontSize: '18px !important',
            fontWeight: '600 !important',
            lineHeight: 1.3,
            padding: '16px 56px 16px 16px !important',
            '& .MuiIconButton-root[aria-label="close"]': {
              marginTop: '0 !important',
              top: '50%',
              transform: 'translateY(-50%)',
            },
          } : undefined}
        >
          {mobileSteps ? mobileStepTitle : '메일 전송'}
        </DialogTitleClose>
        <DialogContent
          sx={threeColumn ? {
            display: 'flex',
            flexDirection: 'column',
            flex: mobileSteps ? 1 : '1 1 auto',
            minHeight: 0,
            overflow: mobileSteps ? 'hidden' : { xs: 'auto', md: 'hidden' },
            ...(mobileSteps ? { p: '12px 12px 8px !important' } : {}),
          } : undefined}
        >
          <Grid
            container
            spacing={2}
            sx={threeColumn ? {
              flex: 1,
              minHeight: 0,
              height: mobileSteps ? '100%' : { md: '100%' },
              alignItems: 'stretch',
              ...(mobileSteps ? { flexWrap: 'nowrap', overflow: 'hidden' } : {}),
            } : undefined}
          >
            <Grid
              item
              xs={12}
              md={threeColumn ? 4 : 12}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                ...(threeColumn ? {
                  minHeight: 0,
                  height: { md: '100%' },
                  overflow: { md: 'hidden' },
                p: '1px',
                boxSizing: 'border-box',
              } : {}),
              ...mobileColumnSx('docs'),
            }}
            >
            {/* 서류 목록 카드 */}
              <Card
                variant="outlined"
                sx={threeColumn ? {
                  flex: mobileSteps ? 1 : { md: 1 },
                  minHeight: mobileSteps ? 0 : { md: 0 },
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                } : undefined}
              >
                <CardHeader
                  titleTypographyProps={{ variant: 'subtitle2' }}
                  title={"서류목록"}
                  sx={mailCardHeaderSx}
                />
                <CardContent
                  sx={threeColumn ? {
                    flex: mobileSteps ? 1 : { md: 1 },
                    minHeight: mobileSteps ? 0 : { md: 0 },
                    overflow: mobileSteps ? 'auto' : { md: 'auto' },
                  } : undefined}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={includeGTestComparisons}
                          onChange={handleIncludeGTestComparisons}
                          disabled={!sendItems.some(item => item.gtestComparisonDocumentPath)}
                        />
                      }
                      label="시험성과대비표 포함"
                      sx={{ m: 0, width: '100%', justifyContent: 'flex-end' }}
                    />
                    <List dense={true}>
                    {sendItems.map((item) => {
                      const folderDoc = item.documentType === 'SUBMAT_FOLDER';
                      const fresh = folderDoc
                        ? Boolean(item.validYN || item.valid)
                        : Boolean(item.name?.validYN || item.name?.valid);
                      const nameColor = item.documentPath
                        ? (fresh ? '#2EA2DE' : '#DB4E2E')
                        : '#9B9B9B';
                      return (
                      <ListItem key={item.id} disablePadding sx={{ p: 0 }}>
                        <ListItemButton>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                            <Chip
                              label={item.name.type}
                              size="small"
                              sx={{
                                bgcolor: item.documentPath
                                  ? (fresh ? '#2EA2DE' : '#DB4E2E')
                                  : '#DBDBDB',
                                color: '#fff',
                              }}
                            />
                            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 'bold', color: nameColor, flex: 1, minWidth: 0 }}>
                              {`${item.name.division === 'RAW_MATERIAL' && item.name.gcomponentItemName ? item.name.gcomponentItemName : ""} ${item.name.name}`}
                            </Typography>
                            {item.name.type === "성적서" && item.name.g04docuGCertificationName && (
                              <Chip label={item.name.g04docuGCertificationName} size="small" />
                            )}
                            {item.gtestComparisonDocumentPath ? (
                              <Chip label="대비표" size="small" variant="outlined" sx={{ ml: 'auto' }} />
                            ) : null}
                            <IconButton
                              size="small"
                              aria-label="이번 보내기에서 빼기"
                              onClick={(e) => handleRemoveSendItem(e, item)}
                              sx={item.gtestComparisonDocumentPath ? undefined : { ml: 'auto' }}
                            >
                              <Clear sx={{ width: 14, height: 14 }} />
                            </IconButton>
                          </Stack>
                        </ListItemButton>
                      </ListItem>
                      );
                    })}
                    </List>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ px: 1, pb: 0.5 }}>
                      <Typography variant="subtitle2">내 컴퓨터에서 추가</Typography>
                      <Button size="small" variant="outlined" component="label">
                        {"파일 선택"}
                        <input
                          hidden
                          type="file"
                          multiple
                          accept="image/*,.pdf"
                          onChange={handleFileChange}
                        />
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, pb: 1 }}>
                      등록 서류와 별도로, 이 메일에만 첨부합니다.
                    </Typography>
                    {addedFiles.length > 0 && (
                    <List dense={true}>
                    {addedFiles.map((file, idx) => (
                      <ListItem key={`added-${idx}`} disablePadding sx={{ p: 0 }}>
                        <ListItemButton>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                            <Chip
                              label="추가"
                              size="small"
                              sx={{ bgcolor: '#6B7280', color: '#fff' }}
                            />
                            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 'bold', flex: 1, minWidth: 0 }}>
                              {file.name}
                            </Typography>
                            <IconButton
                              size="small"
                              aria-label="추가 서류 빼기"
                              onClick={(e) => handleDeleteAddedFile(e, idx)}
                            >
                              <Clear sx={{ width: 14, height: 14 }} />
                            </IconButton>
                          </Stack>
                        </ListItemButton>
                      </ListItem>
                    ))}
                    </List>
                    )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid
              item
              xs={12}
              md={threeColumn ? 4 : 12}
              sx={{
                ...(threeColumn ? {
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  height: { md: '100%' },
                  overflow: { md: 'hidden' },
                  p: '1px',
                  boxSizing: 'border-box',
                } : {}),
                ...mobileColumnSx('stamp'),
              }}
            >
            {/* 원본대조필 카드 */}
              <Card
                variant="outlined"
                sx={threeColumn ? {
                  flex: 1,
                  minHeight: 0,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                } : { flexShrink: 0 }}
              >
                <CardHeader
                  titleTypographyProps={{ variant: 'subtitle2' }}
                  title={"원본대조필"}
                  sx={mailCardHeaderSx}
                />
                <CardContent
                  sx={threeColumn ? {
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                  } : undefined}
                >
                  {useFolderStamp ? (
                    <CertifiedStampPrintPane
                      signOption={signOption}
                      stampLayout={stampLayout}
                      onChangeSignOption={handleFolderSignOption}
                      onChangeStampLayout={setStampLayout}
                      control={control}
                      signThumbnailUrl={URLThumbnail}
                      emptyRecipient={emptyRecipient}
                      emptySite={emptySite}
                      onChangeRecipient={handleChangeRecipient}
                      onChangeSite={handleChangeSite}
                      captionCompact="원본대조필과 인감만 인쇄합니다."
                      captionFull=""
                      header={signOption !== 'NONE' ? (
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="flex-start"
                          sx={{ width: '100%', maxWidth: 640 }}
                        >
                          <Typography variant="body2">
                            원본문서에 직인정보가 없을 경우, 우리회사의 직인으로 대체 날인합니다.
                          </Typography>
                          <Checkbox
                            onChange={handleChangeMyCertified}
                            checked={myCertified}
                            sx={{ mt: -0.4 }}
                          />
                        </Stack>
                      ) : null}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenWithIcon />}
                        onClick={() => setPlacementOpen(true)}
                        disabled={folderPlacementItems.length === 0}
                        sx={{ mt: 1 }}
                      >
                        문서에서 위치 지정
                      </Button>
                      {Object.keys(stampPlacements).length > 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          {`위치 ${Object.keys(stampPlacements).length}곳 지정됨`}
                        </Typography>
                      ) : null}
                      {mixedStamp ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          위치 지정과 간략 날인은 폴더형 서류와 추가한 PDF에 적용됩니다. 레거시 서류는 기본 위치의 전체 양식으로 날인됩니다.
                        </Typography>
                      ) : null}
                    </CertifiedStampPrintPane>
                  ) : (
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <ToggleButtonGroup
                        color="primary"
                        value={signOption}
                        exclusive
                        onChange={handleChangeSignOption}
                      >
                        <ToggleButton value="CERTIFIED_TRUE_COPY_AND_SIGN">
                          {"원본대조필 인쇄"}
                        </ToggleButton>
                        <ToggleButton value="NONE">
                          {"원본대조필 인쇄 안함"}
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Grid>
                    <Grid item xs={12} sx={{ display: signOption === 'NONE' ? 'none' : "" }}>
                      <Stack direction="row" display="flex" justifyContent={"flex-start"} alignItems="center">
                        <Typography variant="body2">
                          {"원본문서에 직인정보가 없을 경우, 우리회사의 직인으로 대체 날인합니다."}
                        </Typography>
                        <Checkbox
                          onChange={handleChangeMyCertified}
                          checked={myCertified}
                          sx={{ mt: -0.4 }}
                        />
                      </Stack>
                    </Grid>
                    {/* 날인 정보 테이블 */}
                    <Grid item xs={12} sx={{ display: signOption === 'NONE' ? 'none' : "" }}>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Grid container>
                            <Grid item xs={12} display="flex" justifyContent={"center"} alignItems="center">
                              <TableContainer>
                                <Table aria-label="날일정보" sx={{ width: 400 }} size="small">
                                  <TableBody>
                                    {/* 원본대조필 행 */}
                                    <TableRow>
                                      <TableCell colspan={2} sx={{ borderTop: 1, borderLeft: 1, borderBottom: 1, borderRight: 1, width: '90%', fontSize: '20px', color: '#664AA0' }}>
                                        <Box display="flex" justifyContent="center" alignItems="center">
                                          {"원본대조필 (인)"}
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ borderTop: 1, borderRight: 1, borderBottom: 1, color: '#664AA0', p: 0 }}>
                                        {URLThumbnail && (
                                          <Box sx={{ width: 60, height: 60 }} display="flex" justifyContent={"center"} alignItems={"center"}>
                                            <img src={URLThumbnail} alt="이미지 미리보기" style={{ maxWidth: '58px' }} />
                                          </Box>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                    {/* 제공사 정보 행 */}
                                    <TableRow>
                                      <TableCell sx={{ borderTop: 1, borderLeft: 1, borderBottom: 1, borderRight: 1, color: '#664AA0', width: '30%' }}>
                                        <Grid container>
                                          <Grid item xs={10}>
                                            <FormInputText
                                              name={"supplierLabel"}
                                              control={control}
                                              variant="standard"
                                              InputProps={{
                                                disableUnderline: true,
                                                readOnly: true,
                                              }}
                                              inputProps={{ style: { fontSize: 14, color: '#664AA0' } }}
                                            />
                                          </Grid>
                                          <Grid item xs={2}>
                                            {" : "}
                                          </Grid>
                                        </Grid>
                                      </TableCell>
                                      <TableCell colSpan={2} sx={{ borderTop: 1, borderRight: 1, borderBottom: 1, color: '#664AA0' }}>
                                        <FormInputText
                                          name={"supplier"}
                                          control={control}
                                          variant="standard"
                                          InputProps={{
                                            disableUnderline: true,
                                            readOnly: true,
                                          }}
                                          inputProps={{ style: { fontSize: 14, color: '#664AA0' } }}
                                        />
                                      </TableCell>
                                    </TableRow>
                                    {/* 제출처 정보 행 */}
                                    <TableRow>
                                      <TableCell sx={{ borderTop: 1, borderLeft: 1, borderBottom: 1, borderRight: 1, width: '30%', fontSize: '14px', color: '#664AA0' }}>
                                        <Grid container>
                                          <Grid item xs={10}>
                                            <FormInputText
                                              name={"recipientLabel"}
                                              control={control}
                                              variant="standard"
                                              InputProps={{
                                                disableUnderline: true,
                                                readOnly: true,
                                              }}
                                              inputProps={{ style: { fontSize: 14, color: '#664AA0' } }}
                                            />
                                          </Grid>
                                          <Grid item xs={2}>
                                            {" : "}
                                          </Grid>
                                        </Grid>
                                      </TableCell>
                                      <TableCell colSpan={2} sx={{ borderTop: 1, borderRight: 1, borderBottom: 1, fontSize: '14px', color: '#664AA0' }}>
                                        <FormInputText
                                          name={"recipient"}
                                          control={control}
                                          variant="standard"
                                          inputProps={{ style: { fontSize: 14, color: '#664AA0' } }}
                                          error={emptyRecipient}
                                          errorStyle={{ message: false, border: true }}
                                          onChange={handleChangeRecipient}
                                        />
                                      </TableCell>
                                    </TableRow>
                                    {/* 현장 정보 행 */}
                                    <TableRow>
                                      <TableCell sx={{ borderTop: 1, borderLeft: 1, borderBottom: 1, borderRight: 1, width: '30%', fontSize: '14px', color: '#664AA0' }}>
                                        <Grid container>
                                          <Grid item xs={10}>
                                            <FormInputText
                                              name={"siteLabel"}
                                              control={control}
                                              variant="standard"
                                              InputProps={{
                                                disableUnderline: true,
                                                readOnly: true,
                                              }}
                                              inputProps={{ style: { fontSize: 14, color: '#664AA0' } }}
                                            />
                                          </Grid>
                                          <Grid item xs={2}>
                                            {" : "}
                                          </Grid>
                                        </Grid>
                                      </TableCell>
                                      <TableCell colSpan={2} sx={{ borderTop: 1, borderRight: 1, borderBottom: 1, fontSize: '14px', color: '#664AA0' }}>
                                        <FormInputText
                                          name={"site"}
                                          control={control}
                                          variant="standard"
                                          inputProps={{ style: { fontSize: 14, color: '#664AA0' } }}
                                          error={emptySite}
                                          errorStyle={{ message: false, border: true }}
                                          onChange={handleChangeSite}
                                        />
                                      </TableCell>
                                    </TableRow>
                                    {/* 문구 정보 행 */}
                                    <TableRow>
                                      <TableCell colSpan={3} sx={{ border: 1, borderColor: '#664AA0' }}>
                                        <FormInputText
                                          name={"phrase"}
                                          control={control}
                                          variant="standard"
                                          InputProps={{
                                            disableUnderline: true,
                                            readOnly: true,
                                          }}
                                          inputProps={{ style: { fontSize: 14, color: '#664AA0' } }}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* 메일 쓰기 카드 */}
            <Grid
              item
              xs={12}
              md={threeColumn ? 4 : 12}
              sx={{
                ...(threeColumn ? {
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  height: { md: '100%' },
                  overflow: { md: 'hidden' },
                  p: '1px',
                  boxSizing: 'border-box',
                } : {}),
                ...mobileColumnSx('mail'),
              }}
            >
              <Card
                variant="outlined"
                sx={threeColumn ? {
                  flex: 1,
                  minHeight: 0,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                } : undefined}
              >
                <CardHeader
                  titleTypographyProps={{ variant: 'subtitle2' }}
                  title={"메일 쓰기"}
                  sx={mailCardHeaderSx}
                />
                <CardContent
                  sx={threeColumn ? {
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                  } : undefined}
                >
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                      '& > .MuiGrid-item': { minWidth: 0, maxWidth: '100%' },
                    }}
                  >
                    <Grid item xs={12}>
                      <FormInputAutoComplete
                        control={control}
                        label={"거래선명"}
                        options={gclients.map(gclient => ({ label: gclient.name, id: gclient.id }))}
                        setValue={setValue}
                        onCustomChange={handleChangeGClient}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <CustomInputSelect
                        defaultOptions={emailOptionsTo}
                        addressOptions={addressOptions}
                        label="받는 사람"
                        placeholder="이메일 선택 또는 직접 입력하세요."
                        onChange={(value) => {
                          setMailTos(value);
                          setValue('mailTo', value);
                        }}
                        width="100%"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <CustomInputSelect
                        defaultOptions={emailOptionsCc}
                        addressOptions={addressOptions}
                        label="참조"
                        placeholder="이메일 선택 또는 직접 입력하세요."
                        onChange={(value) => {
                          setMailCcs(value);
                          setValue('mailCc', value);
                        }}
                        width="100%"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormInputMultipleSelect
                        id="multiple-mail-cc-internal-select"
                        name="mailCcInternal"
                        label={"내부참조"}
                        control={control}
                        setValue={setValue}
                        dynamicItemsBasic={mailCcInternals}
                        onChangeItem={(value) => setMailCcInternals(value.map(i => ({ ...JSON.parse(i) })))}
                        options={emailOptionsCcInternal}
                        chipSize={"small"}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ minWidth: 0 }}>
                      <FormInputText
                        name={"subject"}
                        control={control}
                        label={"제목"}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          ...smallStyle,
                          width: '100%',
                          maxWidth: '100%',
                          minWidth: 0,
                          boxSizing: 'border-box',
                          pb: 1,
                          '& .ql-toolbar, & .ql-container': {
                            boxSizing: 'border-box',
                            width: '100%',
                            maxWidth: '100%',
                          },
                          '& .ql-container': {
                            ...smallStyle['& .ql-container'],
                            height: 'auto',
                            boxSizing: 'border-box',
                            width: '100%',
                            maxWidth: '100%',
                          },
                          '& .ql-editor': {
                            ...smallStyle['& .ql-editor'],
                            height: 'auto',
                            minHeight: '120px',
                            paddingBottom: '12px',
                          },
                        }}
                      >
                        <ReactQuill
                          key={mobileSteps ? `quill-${mobilePhase}` : 'quill'}
                          value={getValues('html')}
                          onChange={(content) => {
                            setValue('html', content);
                          }}
                          modules={fontModules}
                          formats={fontFormats}
                          placeholder="메시지를 입력하세요..."
                          theme="snow"
                        />
                      </Box>
                    </Grid>
                    
                    
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          {threeColumn && !mobileSteps && (
            <Grid container spacing={2} sx={{ flexShrink: 0, pt: 1.5 }}>
              <Grid item md={8} sx={{ display: { xs: 'none', md: 'block' } }} />
              <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end', p: '1px' }}>
                <LoadingButton
                  variant="contained"
                  onClick={handleSubmit(onSubmit)}
                  loading={loading}
                >
                  {"서류 보내기"}
                </LoadingButton>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        {mobileSteps && (
          <DialogActions
            disableSpacing
            sx={{
              px: 2,
              pt: '12px !important',
              pb: 'calc(12px + env(safe-area-inset-bottom)) !important',
              gap: 1,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Button
              variant="outlined"
              disabled={mobilePhase === 'docs'}
              onClick={() => setMobilePhase(mobilePhase === 'mail' ? 'stamp' : 'docs')}
              sx={{ flex: 1, height: 44 }}
            >
              이전
            </Button>
            {mobilePhase === 'mail' ? (
              <LoadingButton
                variant="contained"
                onClick={handleSubmit(onSubmit)}
                loading={loading}
                sx={{ flex: 1, height: 44 }}
              >
                서류 보내기
              </LoadingButton>
            ) : (
              <Button
                variant="contained"
                onClick={() => setMobilePhase(mobilePhase === 'docs' ? 'stamp' : 'mail')}
                sx={{ flex: 1, height: 44 }}
              >
                다음
              </Button>
            )}
          </DialogActions>
        )}
        {!threeColumn && (
        <DialogActions sx={{ pt: 2, pb: 2, pr: 3 }}>
          <LoadingButton
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            loading={loading}
          >
            {"서류 보내기"}
          </LoadingButton>
        </DialogActions>
        )}
      </Dialog>
    
      {useFolderStamp && (
        <CertifiedStampPlacementDialog
          open={placementOpen}
          onClose={() => setPlacementOpen(false)}
          items={folderPlacementItems}
          extras={placementExtras}
          extraSectionTitle="별도 첨부"
          bodyHeaderPaging={false}
          placements={stampPlacements}
          onChangePlacements={setStampPlacements}
          stampLayout={stampLayout}
          signThumbnailUrl={URLThumbnail}
        />
      )}
      <AlertDialog
        alertInfo={alertInfo}
        setAlertInfo={setAlertInfo}
      />
      <AddressBookDialogRegister
        crudMode={crudMode}
        setCrudMode={setCrudMode}
        open={addressBookDialogOpen}
        setOpen={setAddressBookDialogOpen}
        selectedRow={selectedRow}
        onSuccess={() => {
          setAddressBookDialogOpen(false);
          setCrudMode('');
          setSelectedRow(null);
        }}
      />
    </>
  );
};

export default SendMailDialog;